/**
 * TikTok Settings Panel
 * FULLY Compliant with TikTok Content Sharing API UX Guidelines (Points 1-5)
 * 
 * Requirements implemented:
 * 1. Query creator_info API for privacy_level_options + show creator nickname
 * 2. Privacy dropdown (NO default) + interaction toggles + Music Usage consent
 * 3. Commercial content toggle (OFF by default) + "Your Brand" / "Branded Content" checkboxes
 * 4. Privacy restriction for Branded Content + updated consent text
 * 5. Full content preview (handled by parent)
 * 
 * Ref: https://developers.tiktok.com/doc/content-sharing-guidelines
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { apiClient } from "@/lib/api";
import { 
  Info, 
  Globe, 
  Users, 
  Lock, 
  MessageCircle, 
  Film, 
  Scissors,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Tag,
  BadgeCheck,
  UserCircle
} from "lucide-react";

// Default privacy options if API fails
const DEFAULT_PRIVACY_OPTIONS = [
  { value: "PUBLIC_TO_EVERYONE", label: "Everyone", icon: Globe, description: "Anyone on TikTok can view" },
  { value: "MUTUAL_FOLLOW_FRIENDS", label: "Friends", icon: Users, description: "Only mutual followers" },
  { value: "FOLLOWER_OF_CREATOR", label: "Followers", icon: Users, description: "Only your followers" },
  { value: "SELF_ONLY", label: "Only Me", icon: Lock, description: "Private - only you can view" },
];

export default function TikTokSettingsPanel({ 
  settings, 
  onSettingsChange, 
  onConsentChange,
  isPhotoPost = false,
  disabled = false 
}) {
  // Point 1: Creator info state
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [privacyOptions, setPrivacyOptions] = useState([]);
  const [loadingCreatorInfo, setLoadingCreatorInfo] = useState(true);
  const [creatorError, setCreatorError] = useState(null);
  
  // Point 2: TikTok requires NO defaults - user must actively select
  const [privacyLevel, setPrivacyLevel] = useState("");
  const [allowComment, setAllowComment] = useState(false);  // OFF by default per guidelines
  const [allowDuet, setAllowDuet] = useState(false);        // OFF by default per guidelines
  const [allowStitch, setAllowStitch] = useState(false);    // OFF by default per guidelines
  
  // Point 3: Commercial disclosure toggle (OFF by default) + checkboxes
  const [disclosureEnabled, setDisclosureEnabled] = useState(false);
  const [isYourBrand, setIsYourBrand] = useState(false);      // "Your Brand" checkbox
  const [isBrandedContent, setIsBrandedContent] = useState(false);  // "Branded Content" checkbox
  
  // Point 5: Consent checkbox
  const [hasConsented, setHasConsented] = useState(false);
  
  // Validation state
  const isPrivacySelected = privacyLevel !== "";
  const isValid = isPrivacySelected && hasConsented;
  
  // Point 4: Branded content requires public/friends visibility
  const isPrivate = privacyLevel === "SELF_ONLY";
  const brandedDisabled = isPrivate || !isPrivacySelected;
  
  // Point 1: Fetch creator info on mount
  useEffect(() => {
    fetchCreatorInfo();
  }, []);
  
  const fetchCreatorInfo = async () => {
    try {
      setLoadingCreatorInfo(true);
      setCreatorError(null);
      
      const response = await apiClient.get("/social/tiktok/creator-info");
      
      if (response.data.creator_info) {
        setCreatorInfo(response.data.creator_info);
        
        // Use privacy_level_options from API if available
        if (response.data.creator_info.privacy_level_options?.length > 0) {
          const apiOptions = response.data.creator_info.privacy_level_options.map(option => ({
            value: option,
            label: formatPrivacyLabel(option),
            description: formatPrivacyDescription(option),
            icon: getPrivacyIcon(option)
          }));
          setPrivacyOptions(apiOptions);
        } else {
          setPrivacyOptions(DEFAULT_PRIVACY_OPTIONS);
        }
      } else {
        setPrivacyOptions(DEFAULT_PRIVACY_OPTIONS);
      }
    } catch (error) {
      console.error("Failed to fetch TikTok creator info:", error);
      setCreatorError("Could not verify TikTok account. Using default settings.");
      setPrivacyOptions(DEFAULT_PRIVACY_OPTIONS);
    } finally {
      setLoadingCreatorInfo(false);
    }
  };
  
  // Helper functions for privacy options
  const formatPrivacyLabel = (value) => {
    const labels = {
      "PUBLIC_TO_EVERYONE": "Everyone",
      "MUTUAL_FOLLOW_FRIENDS": "Friends",
      "FOLLOWER_OF_CREATOR": "Followers",
      "SELF_ONLY": "Only Me"
    };
    return labels[value] || value;
  };
  
  const formatPrivacyDescription = (value) => {
    const descriptions = {
      "PUBLIC_TO_EVERYONE": "Anyone on TikTok can view",
      "MUTUAL_FOLLOW_FRIENDS": "Only mutual followers",
      "FOLLOWER_OF_CREATOR": "Only your followers",
      "SELF_ONLY": "Private - only you can view"
    };
    return descriptions[value] || "";
  };
  
  const getPrivacyIcon = (value) => {
    if (value === "PUBLIC_TO_EVERYONE") return Globe;
    if (value === "SELF_ONLY") return Lock;
    return Users;
  };
  
  // Point 4: Auto-disable branded content when privacy is private
  useEffect(() => {
    if (isPrivate && isBrandedContent) {
      setIsBrandedContent(false);
      // Show notification to user about privacy restriction
    }
  }, [privacyLevel]);
  
  // Update parent when settings change
  useEffect(() => {
    onSettingsChange?.({
      privacyLevel,
      allowComment,
      allowDuet: isPhotoPost ? false : allowDuet,
      allowStitch: isPhotoPost ? false : allowStitch,
      disclosureEnabled,
      isYourBrand,
      isBrandedContent,
      creatorNickname: creatorInfo?.creator_nickname,
      isValid
    });
  }, [privacyLevel, allowComment, allowDuet, allowStitch, disclosureEnabled, isYourBrand, isBrandedContent, isValid, isPhotoPost, creatorInfo]);
  
  // Update consent state to parent
  useEffect(() => {
    onConsentChange?.(hasConsented && isValid);
  }, [hasConsented, isValid]);
  
  // Point 4: Generate consent text based on disclosure selections
  const getConsentText = () => {
    if (isBrandedContent) {
      return "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation.";
    }
    return "By posting, you agree to TikTok's Music Usage Confirmation.";
  };

  return (
    <div className="space-y-5">
      {/* TikTok Policy Notice */}
      <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-purple-300 font-medium">TikTok Platform Requirement</p>
            <p className="text-xs text-purple-400/80 mt-1">
              TikTok requires you to review and confirm posting settings before each post. 
              This ensures you have full control over your content visibility and interactions.
            </p>
          </div>
        </div>
      </div>
      
      {/* Point 1: Creator Info Display */}
      {loadingCreatorInfo ? (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
          <span className="text-sm text-viraly-muted">Verifying TikTok account...</span>
        </div>
      ) : creatorInfo ? (
        <div className="p-4 rounded-xl bg-purple-500/5 border border-purple-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
              {creatorInfo.creator_avatar_url ? (
                <img src={creatorInfo.creator_avatar_url} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <UserCircle className="w-6 h-6 text-purple-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium flex items-center gap-1">
                Posting to: <span className="text-purple-400">@{creatorInfo.creator_nickname || creatorInfo.creator_username}</span>
                <BadgeCheck className="w-4 h-4 text-purple-400" />
              </p>
              <p className="text-xs text-viraly-muted">TikTok account verified</p>
            </div>
          </div>
        </div>
      ) : creatorError ? (
        <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-xs text-yellow-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {creatorError}
          </p>
        </div>
      ) : null}
      
      {/* Point 1 & 2: Privacy Level Selection - NO DEFAULT */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <Globe className="w-4 h-4 text-viraly-orange" />
          Who can view this video? <span className="text-red-400">*</span>
        </label>
        <select
          value={privacyLevel}
          onChange={(e) => setPrivacyLevel(e.target.value)}
          disabled={disabled || loadingCreatorInfo}
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white focus:outline-none focus:border-viraly-orange transition-colors ${
            privacyLevel === "" 
              ? "border-yellow-500/50 text-viraly-muted" 
              : "border-white/20"
          }`}
          data-testid="tiktok-privacy-select"
        >
          <option value="" disabled className="bg-viraly-card">
            Select who can view...
          </option>
          {privacyOptions.map((option) => (
            <option 
              key={option.value} 
              value={option.value} 
              className="bg-viraly-card"
            >
              {option.label}
            </option>
          ))}
        </select>
        {privacyLevel === "" && (
          <p className="text-xs text-yellow-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Please select a visibility option (required by TikTok)
          </p>
        )}
        {privacyLevel && (
          <p className="text-xs text-viraly-muted">
            {privacyOptions.find(o => o.value === privacyLevel)?.description}
          </p>
        )}
      </div>
      
      {/* Point 2: Interaction Controls - ALL OFF BY DEFAULT */}
      <div className="space-y-3">
        <label className="text-sm font-medium">Interaction Settings</label>
        <p className="text-xs text-viraly-muted -mt-1">All interactions are off by default per TikTok guidelines.</p>
        
        <div className="space-y-2">
          {/* Allow Comments */}
          <label 
            className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 cursor-pointer transition-colors"
            data-testid="tiktok-allow-comment"
          >
            <input
              type="checkbox"
              checked={allowComment}
              onChange={(e) => setAllowComment(e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 rounded border-white/30 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
            />
            <MessageCircle className="w-4 h-4 text-viraly-muted" />
            <div className="flex-1">
              <span className="text-sm">Allow Comments</span>
              <p className="text-xs text-viraly-muted">Let viewers comment on your post</p>
            </div>
          </label>
          
          {/* Allow Duet - Only for videos */}
          {!isPhotoPost && (
            <label 
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 cursor-pointer transition-colors"
              data-testid="tiktok-allow-duet"
            >
              <input
                type="checkbox"
                checked={allowDuet}
                onChange={(e) => setAllowDuet(e.target.checked)}
                disabled={disabled}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
              />
              <Film className="w-4 h-4 text-viraly-muted" />
              <div className="flex-1">
                <span className="text-sm">Allow Duet</span>
                <p className="text-xs text-viraly-muted">Let others create duets with your video</p>
              </div>
            </label>
          )}
          
          {/* Allow Stitch - Only for videos */}
          {!isPhotoPost && (
            <label 
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 cursor-pointer transition-colors"
              data-testid="tiktok-allow-stitch"
            >
              <input
                type="checkbox"
                checked={allowStitch}
                onChange={(e) => setAllowStitch(e.target.checked)}
                disabled={disabled}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
              />
              <Scissors className="w-4 h-4 text-viraly-muted" />
              <div className="flex-1">
                <span className="text-sm">Allow Stitch</span>
                <p className="text-xs text-viraly-muted">Let others use clips from your video</p>
              </div>
            </label>
          )}
        </div>
      </div>
      
      {/* Point 3: Commercial Content Disclosure - Toggle OFF by default */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium flex items-center gap-2">
            <Tag className="w-4 h-4 text-viraly-orange" />
            Disclose Commercial Content
          </label>
          <button
            type="button"
            onClick={() => setDisclosureEnabled(!disclosureEnabled)}
            disabled={disabled}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              disclosureEnabled ? "bg-purple-500" : "bg-white/20"
            }`}
            data-testid="tiktok-disclosure-toggle"
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
              disclosureEnabled ? "translate-x-7" : "translate-x-1"
            }`} />
          </button>
        </div>
        <p className="text-xs text-viraly-muted">
          Turn on if this content promotes yourself, a third party, or is a paid partnership.
        </p>
        
        {/* Point 3: Checkboxes for "Your Brand" and "Branded Content" */}
        {disclosureEnabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-2 pt-2"
          >
            {/* Your Brand checkbox */}
            <label 
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                isYourBrand 
                  ? "bg-orange-500/10 border-orange-500/50" 
                  : "bg-white/5 border-white/10 hover:border-white/20"
              }`}
              data-testid="tiktok-your-brand"
            >
              <input
                type="checkbox"
                checked={isYourBrand}
                onChange={(e) => setIsYourBrand(e.target.checked)}
                disabled={disabled}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-orange-500 focus:ring-orange-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">Your Brand</span>
                <p className="text-xs text-viraly-muted">
                  You are promoting yourself or your own business.
                  {isYourBrand && <span className="text-orange-400 block mt-1">Post will be labeled "Promotional content"</span>}
                </p>
              </div>
            </label>
            
            {/* Branded Content checkbox */}
            <label 
              className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                brandedDisabled 
                  ? "opacity-50 cursor-not-allowed bg-white/5 border-white/10" 
                  : isBrandedContent
                    ? "bg-purple-500/10 border-purple-500/50 cursor-pointer"
                    : "bg-white/5 border-white/10 hover:border-white/20 cursor-pointer"
              }`}
              data-testid="tiktok-branded-content"
            >
              <input
                type="checkbox"
                checked={isBrandedContent}
                onChange={(e) => !brandedDisabled && setIsBrandedContent(e.target.checked)}
                disabled={disabled || brandedDisabled}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-purple-500 focus:ring-purple-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">Branded Content</span>
                <p className="text-xs text-viraly-muted">
                  {brandedDisabled 
                    ? "Branded content can't be private. Select public or friends visibility first."
                    : "You are promoting a third party brand or have a paid partnership."
                  }
                  {isBrandedContent && !brandedDisabled && (
                    <span className="text-purple-400 block mt-1">Post will be labeled "Paid partnership"</span>
                  )}
                </p>
              </div>
            </label>
            
            {/* Point 4: Notice about branded content visibility */}
            {isPrivate && disclosureEnabled && (
              <p className="text-xs text-yellow-400 flex items-center gap-1 p-2 bg-yellow-500/10 rounded-lg">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                Visibility for branded content can't be private. Change visibility to enable branded content.
              </p>
            )}
          </motion.div>
        )}
      </div>
      
      {/* Point 2 & 4: Explicit Consent with dynamic text */}
      <div className="pt-4 border-t border-white/10">
        <label 
          className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
            hasConsented 
              ? "bg-green-500/10 border-green-500/50" 
              : "bg-white/5 border-yellow-500/50"
          }`}
          data-testid="tiktok-consent-checkbox"
        >
          <input
            type="checkbox"
            checked={hasConsented}
            onChange={(e) => setHasConsented(e.target.checked)}
            disabled={disabled || !isPrivacySelected}
            className="w-5 h-5 mt-0.5 rounded border-white/30 bg-white/10 text-green-500 focus:ring-green-500"
          />
          <div className="flex-1">
            <span className="text-sm font-medium flex items-center gap-2">
              {hasConsented && <CheckCircle2 className="w-4 h-4 text-green-400" />}
              I confirm these TikTok settings
            </span>
            <p className="text-xs text-viraly-muted mt-1">
              {getConsentText()}
            </p>
            <p className="text-xs text-viraly-muted mt-2">
              I have reviewed the visibility, interaction, and content disclosure settings above and authorize Viraly to post to TikTok with these settings.
            </p>
          </div>
        </label>
        
        {!hasConsented && isPrivacySelected && (
          <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Please confirm the settings to continue
          </p>
        )}
        {!isPrivacySelected && (
          <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Please select a visibility option first
          </p>
        )}
      </div>
      
      {/* TikTok Guidelines Links */}
      <div className="flex flex-wrap justify-center gap-4 pt-2">
        <a 
          href="https://www.tiktok.com/legal/page/global/bc-policy" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-viraly-muted hover:text-viraly-orange inline-flex items-center gap-1"
        >
          Branded Content Policy <ExternalLink className="w-3 h-3" />
        </a>
        <a 
          href="https://www.tiktok.com/legal/page/global/music-usage-confirmation" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-viraly-muted hover:text-viraly-orange inline-flex items-center gap-1"
        >
          Music Usage Confirmation <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
